/**
 * LOS (Loan Origination System) constants — Batch 1.
 *
 * Single source of truth for LOS-related enums that cross module
 * boundaries (DB → backend → frontend). String values pinned as
 * `as const` to catch typos at compile-time.
 *
 * Sprint-1 of the 17-point checklist work loads LOS data via JSON
 * upload (`json_upload` adapter). When the real Axis LOS REST API
 * becomes available, only the adapter changes; the constants below
 * remain stable.
 */
// -------------------------------------------------------------------------
// LOS data source adapter
// -------------------------------------------------------------------------
export const LOS_ADAPTER = {
    /** Mock / demo / development: data uploaded as a JSON file already in
     *  canonical LosApplicationData shape. */
    JSON_UPLOAD: 'json_upload',
    /** Raw Axis LOS `underwritinginfo` response, uploaded as-is and mapped
     *  to the canonical shape by the Axis mapper. Use this while the REST
     *  pull is not yet wired. */
    AXIS_JSON: 'axis_json',
    /** Production: data pulled from real LOS REST API. */
    REST_API: 'rest_api',
};
// -------------------------------------------------------------------------
// Applicant role within a loan file
// -------------------------------------------------------------------------
export const APPLICANT_ROLE = {
    PRIMARY: 'primary',
    CO_APPLICANT: 'co-applicant',
    GUARANTOR: 'guarantor',
};
// -------------------------------------------------------------------------
// API verification status from LOS (point 2 of checklist)
// -------------------------------------------------------------------------
export const LOS_API_STATUS = {
    SUCCESS: 'Success',
    FAILED: 'Failed',
    PENDING: 'Pending',
};
// -------------------------------------------------------------------------
// Posidex (Dedupe) status — point 3
// -------------------------------------------------------------------------
export const POSIDEX_STATUS = {
    VALIDATED: 'Validated',
    PENDING: 'Pending',
    FAILED: 'Failed',
};
// -------------------------------------------------------------------------
// Hunter verification result — point 5
// -------------------------------------------------------------------------
export const HUNTER_RESULT = {
    CLEAN: 'Clean',
    NEGATIVE: 'Negative',
};
// -------------------------------------------------------------------------
// BQS verification result — point 6
// -------------------------------------------------------------------------
export const BQS_RESULT = {
    NO_MATCH: 'No Match',
    NEGATIVE_R1: 'Negative R1',
    NEGATIVE_R2: 'Negative R2',
};
// -------------------------------------------------------------------------
// RCU verification — point 7
// -------------------------------------------------------------------------
export const RCU_TYPE = {
    INTERNAL: 'Internal',
    EXTERNAL: 'External',
    WAIVED: 'Waived',
};
export const RCU_OUTCOME = {
    POSITIVE: 'Positive',
    NEGATIVE: 'Negative',
    PENDING: 'Pending',
};
// -------------------------------------------------------------------------
// Deviation status — point 13
// -------------------------------------------------------------------------
export const DEVIATION_STATUS = {
    APPROVED: 'approved',
    PENDING: 'pending',
    REJECTED: 'rejected',
};
/**
 * Canonical deviation types. Used to link deviations to the rule that
 * "consumed" them — e.g. a hunter_negative deviation justifies a Hunter
 * negative match.
 */
export const DEVIATION_TYPE = {
    HUNTER_NEGATIVE: 'hunter_negative',
    BQS_NEGATIVE: 'bqs_negative',
    RCU_NEGATIVE: 'rcu_negative',
    CIBIL_BELOW_THRESHOLD: 'cibil_below_threshold',
    DEDUPE_PENDING: 'dedupe_pending',
    COMPANY_CAP_EXCEEDED: 'company_cap_exceeded',
    VALIDITY_EXCEEDED: 'validity_exceeded',
    OTHER: 'other',
};
// -------------------------------------------------------------------------
// E-Agreement interface status — point 16
// -------------------------------------------------------------------------
export const E_AGREEMENT_INTERFACE_STATUS = {
    SUCCESS: 'Success',
    FAILED: 'Failed',
    PENDING: 'Pending',
};
// -------------------------------------------------------------------------
// Cash flow assessment method — point 9
// -------------------------------------------------------------------------
export const ASSESSMENT_METHOD = {
    SALARIED: 'Salaried',
    BANKING_SURROGATE: 'Banking Surrogate',
    GST_PROGRAM: 'GST Program',
    ITR_PROGRAM: 'ITR Program',
    CASH_FLOW: 'Cash Flow',
};
// -------------------------------------------------------------------------
// Masters types added for LOS audit (Batch 1)
// -------------------------------------------------------------------------
export const LOS_MASTERS_TYPE = {
    /** Per product → minimum (or maximum) CIBIL score threshold. Point 4. */
    CIBIL_THRESHOLDS: 'cibil_thresholds',
    /** Per company category → maximum loan amount cap. Point 12. */
    COMPANY_CATEGORIZATION: 'company_categorization',
    /** Per deviation level → authorized approver employee IDs. Point 8. */
    DEVIATION_APPROVERS: 'deviation_approvers',
    /** Maps deviation types to required approval levels. Point 8. */
    DEVIATION_LEVEL_MAPPING: 'deviation_level_mapping',
    /** Loan size × product → required approver level. Used across deviation rules. */
    DOA_MATRIX: 'doa_matrix',
    /** Field-name mapping from raw LOS export to internal schema (forward-compat). */
    LOS_FIELD_TAXONOMY: 'los_field_taxonomy',
    /**
     * Per product → full audit policy. Drives the V2 12-point CAM-vs-LOS
     * rules to behave correctly for each product without code changes.
     *
     * Adding a new product is one INSERT into this table. The rules
     * read `ctx.productPolicy.get(productCode)` to learn:
     *   - which CIBIL threshold applies (and operator)
     *   - whether the product is secured (affects Points 10/11)
     *   - which income methods are allowed (Points 3/4/5)
     *   - LTV regulatory cap bands by amount
     *   - max tenure, applicant age bounds, KYC min per applicant
     *   - sanction → disbursement validity window in days
     *   - valuation staleness window (for secured loans)
     *   - which of the 12 checklist points apply to this product
     */
    PRODUCT_POLICY: 'product_policy',
};
// -------------------------------------------------------------------------
// LOS-related admin config keys
// -------------------------------------------------------------------------
export const LOS_CONFIG_KEY = {
    /** Which adapter to use ('json_upload' | 'rest_api'). Default 'json_upload'. */
    ADAPTER_TYPE: 'los.adapter_type',
    /** Whether a loan file MUST have LOS data attached before validation runs. */
    REQUIRED: 'los.data_required',
    /** REST API base URL for production adapter (unused in json_upload mode). */
    REST_API_BASE_URL: 'los.rest_api_base_url',
    /**
     * JSON object mapping the raw LOS PRODUCT_CODE ("AHL - Fixed", "PL_Flexi")
     * to our masters product code ("HOME_LOAN", "PL"). Without it, product-keyed
     * masters lookups miss and product rules go inconclusive.
     */
    PRODUCT_CODE_MAP: 'los.product_code_map',
    /**
     * JSON object mapping SZ_PORTFOLIO_CODE ("AH", "MM", "ML", "AFH") to our
     * masters product code. Used to attribute deviation-matrix rows to products;
     * a code may list several portfolios ("MM,AH") and then belongs to each.
     */
    PORTFOLIO_CODE_MAP: 'los.portfolio_code_map',
};
// -------------------------------------------------------------------------
// LOS-related permissions
// -------------------------------------------------------------------------
//
// These follow the existing pattern in shared/src/constants/permissions.ts.
// They are *defined* here for the LOS module's own consumption; the
// permissions module's registry imports them.
export const LOS_PERMISSIONS = {
    /** Upload LOS data (JSON or trigger REST pull) for a loan file. */
    LOS_UPLOAD: 'los.upload',
    /** View LOS data attached to a loan file. */
    LOS_VIEW: 'los.view',
    /** Delete/replace LOS data on a loan file (admin only). */
    LOS_MANAGE: 'los.manage',
};
//# sourceMappingURL=los.js.map